/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import keysToConstants from 'js/lib/keysToConstants';

import { Dialog, Typography2 } from '@coursera/cds-core';

import { getFreeTrialRemovalCopies } from 'bundles/coursera-plus-common/components/elevateLTV/prePurchaseUpsell/PrePurchaseUpsellEnrollCopies';
import type { ApiError } from 'bundles/enroll-common/utils/errorUtils';
import { useEnrollInS12nSubscription } from 'bundles/enroll-common/utils/useEnrollInS12nSubscription';
import EnrollErrorModal from 'bundles/enroll-modal/components/common/EnrollErrorModal';
import PromotionApplicableCheckoutMessage from 'bundles/enroll-modal/components/common/PromotionApplicableCheckoutMessage';
import S12nFreeTrialRemovalValueProps from 'bundles/enroll-modal/components/s12n-subscriptions/S12nFreeTrialRemovalValueProps';
import S12nSubscriptionEnrollButton from 'bundles/enroll-modal/components/s12n-subscriptions/S12nSubscriptionEnrollButton';
import usePageData from 'bundles/enroll-modal/data/usePageData';
import { useEnrollModalEventing } from 'bundles/enroll-modal/utils/eventingUtils';

const MODAL_TYPES = keysToConstants(['ENROLL', 'ERROR']);

const styles = {
  // Tuck the subtitle directly under the title (Figma groups them at 4px).
  header: css`
    padding-bottom: var(--cds-spacing-50);
  `,
  // Drop the dialog content's default top padding so the 4px title/subtitle gap holds.
  content: css`
    padding-top: 0;
  `,
  subtitle: css`
    margin-bottom: var(--cds-spacing-300);
  `,
  // Match Figma's 24px action padding so the CTA left-aligns with the content.
  actions: css`
    padding: var(--cds-spacing-300);
  `,
};

type PropsFromCaller = {
  onClose: () => void;
  onSdp: boolean;
  isFromS12nSelection?: boolean;
};

/**
 * Subscription enroll modal shown to the free-trial-removal treatment arm. It
 * reframes the paid-subscribe flow around a 7-day refund guarantee instead of a free trial.
 * This is the treatment-only sibling of S12nSubscriptionEnrollModal / ...V2 — the control modals
 * stay unchanged, and routing selects this one when the treatment is active.
 */
const S12nSubscriptionEnrollModalNoFreeTrial = ({ onClose, onSdp, isFromS12nSelection = false }: PropsFromCaller) => {
  const [activeModal, setActiveModal] = useState<keyof typeof MODAL_TYPES | null>(MODAL_TYPES.ENROLL);
  const [error, setError] = useState<ApiError | undefined>();
  const [enrolling, setEnrolling] = useState(false);
  const { getEnrollModalTrackingRef, trackEnrollModalClose } = useEnrollModalEventing();
  const visibilityTrackingRef = getEnrollModalTrackingRef();
  const { s12n, course, s12nDerivatives, s12nSubscriptionOwnableProduct, isSpecialization } = usePageData();
  const { s12nMonthlySkuId, enrollInSubscription, hasExhaustedFreeTrial } =
    useEnrollInS12nSubscription(isFromS12nSelection);

  const s12nSubscriptionPrice = s12nSubscriptionOwnableProduct?.price ?? s12nDerivatives?.catalogPrice;
  if (!s12n || !s12nMonthlySkuId || !s12nSubscriptionPrice) {
    return null;
  }

  const { HeadingGroup, Content, Actions } = Dialog;

  const copy = getFreeTrialRemovalCopies({
    s12nSubscriptionPrice,
    s12nProductVariant: s12n.productVariant,
    s12n,
    course,
    hasExhaustedFreeTrial,
  });

  const handleClose = () => {
    trackEnrollModalClose();
    onClose();
  };

  const handleError = (apiError: ApiError) => {
    setEnrolling(false);
    setActiveModal(MODAL_TYPES.ERROR);
    setError(apiError);
  };

  const onClickSubscribeCta = () => {
    setEnrolling(true);
    enrollInSubscription().catch(handleError);
  };

  switch (activeModal) {
    case MODAL_TYPES.ENROLL:
      return (
        <Dialog
          data-testid="subscription-enroll-modal-no-free-trial"
          open
          aria-describedby="dialog-content"
          width="medium"
          variant="standard"
          ref={visibilityTrackingRef}
          sticky="actions"
          onClose={handleClose}
        >
          <HeadingGroup css={styles.header}>{copy.title}</HeadingGroup>
          <Content id="dialog-content" css={styles.content}>
            <PromotionApplicableCheckoutMessage course={course} s12n={s12n} isFromS12nSelection={isFromS12nSelection} />
            <Typography2 variant="bodyPrimary" component="div" color="supportText" css={styles.subtitle}>
              {onSdp ? copy.sdpSubtitle : copy.courseSubtitle}
            </Typography2>
            <S12nFreeTrialRemovalValueProps valueProps={copy.valueProps} />
          </Content>
          <Actions css={styles.actions}>
            <S12nSubscriptionEnrollButton
              isFromS12nSelection={isFromS12nSelection}
              showCds={true}
              onClick={onClickSubscribeCta}
              enrolling={enrolling}
            />
          </Actions>
        </Dialog>
      );
    case MODAL_TYPES.ERROR:
      return (
        <EnrollErrorModal
          error={error}
          onClose={handleClose}
          isFinancialAid={false}
          isFreeEnroll={false}
          courseId={course?.id}
          s12nId={s12n?.id}
          isSpecialization={isSpecialization}
        />
      );
    default:
      return null;
  }
};

export default S12nSubscriptionEnrollModalNoFreeTrial;
